import React, { useEffect, useState } from 'react'
import LayoutSingleView from '../../common/layout/LayoutSingleView'
import Section from '../../common/sections/Section'
import { MenuItems } from '../../../enums/MenuItems'
import Button from '../../common/form/inputs/Button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUserPlus } from '@fortawesome/free-solid-svg-icons'
import { Navigate, useSearchParams } from 'react-router-dom'
import Note from '../../common/note/Note'
import { NoteType } from '../../../enums/NoteType'
import Card from '../../common/cards/Card'
import { AuthHelper } from '../../../auth/AuthHelper'
import { useSafeUserData } from '../../../stateStores/User'
import { UserRoles } from '../../../api/tenant/models/UserDto'
import { Pages } from '../../../Pages'
import LoadingSpinner from '../../common/loading/LoadingSpinner'
import RoleCard from '../RoleCard'
import roleApi from '../../../api/role/RoleApi'
import { RoleLiteDto } from '../../../api/role/models/RoleDto'
import personApi from '../../../api/person/PersonApi'
import { PersonLiteDto } from '../../../api/person/models/PersonDto'
import { CandidateReviewStatus } from '../../../api/role/models/CandidateReviewStatus'
import { SharedWithUserLite } from '../../../api/role/models/getSharedWithUsersWithStatus/GetSharedWithUsersWithStatus'
import TrustedCircleList from './TrustedCircleList'
import { RecommendedCandidateWithRecsDto } from '../../../api/role/models/getRecommendedCandidatesForRole/GetRecommendedCandidatesForRole'
import RecommendedCandidatesList from './RecommendedCandidatesList'
// New imports for network + companies
import networkApi from '../../../api/network/NetworkApi'
import companyApi from '../../../api/company/CompanyApi'
import {
    transformNetworkScopedRes,
    TransformedResolvedPerson,
} from '../../../utils/networkTransformUtils'
import { CompanyDto } from '../../../api/company/models/CompanyDto'
import { PersonDto } from '../../../api/person/models/PersonDto'
import { MapExtra } from '../../../utils/MapExtra'
import InviteUsersModal from './InviteUsersModal'

export default function ManageRoleHM(): React.JSX.Element {
    const [searchParams] = useSearchParams()
    const roleId = searchParams.get('id')
    const [isLoading, setIsLoading] = useState(true)
    const [errMsg, setErrMsg] = useState<string | undefined>()
    const { user, tenant } = useSafeUserData()
    const [isModalOpen, setIsModalOpen] = useState(false)

    const [roleLite, setRoleLite] = useState<RoleLiteDto>()
    const [redirectToRoles, setRedirectToRoles] = useState(false)
    // const [apiError, setApiError] = useState<string | undefined>(undefined)
    const [, setPendingHiringManagerIds] = useState<string[]>([])
    const [sharedWith, setSharedWith] = useState<
        {
            user: SharedWithUserLite
            candidateReviewStatus: CandidateReviewStatus
        }[]
    >([])
    const [personById, setPersonById] = useState<
        MapExtra<string, PersonLiteDto>
    >(new MapExtra())
    // Full candidate persons for profile modal
    const [candidatePersonsById, setCandidatePersonsById] = useState<
        MapExtra<string, PersonDto>
    >(new MapExtra())
    const [recommendedCandidates, setRecommendedCandidates] = useState<
        RecommendedCandidateWithRecsDto[]
    >([])

    // New state: connections and companies for rendering candidate connections
    const [connectionsByPersonId, setConnectionsByPersonId] = useState<
        Map<string, TransformedResolvedPerson>
    >(new Map())
    const [companiesById, setCompaniesById] = useState<
        MapExtra<string, CompanyDto>
    >(new MapExtra())

    useEffect(() => {
        if (!roleId) return
        const load = async () => {
            setIsLoading(true)
            try {
                const { role } = await roleApi.getLite({
                    roleId,
                    includeHiringTeamIds: true,
                    includeSharedWithUserIds: true,
                })
                setRoleLite(role)

                // Fetch shared-with users with status and their person lites for avatars/names
                const [sharedWithRes, personsLite, recsRes] = await Promise.all(
                    [
                        roleApi.getSharedWithUsersWithStatus({ roleId }),
                        personApi.getPersonsLite({
                            personIds: role.hiringTeamIds ?? [],
                        }),
                        roleApi.getRecommendedCandidatesForRole({ roleId }),
                    ],
                )

                setSharedWith(sharedWithRes.sharedWith)
                setPersonById(
                    new MapExtra(personsLite.persons.map((p) => [p.id, p])),
                )
                setRecommendedCandidates(recsRes.candidates)
                setErrMsg(undefined)

                // Fetch full person details for all recommended candidates for the profile modal
                const candidateIds = recsRes.candidates.map(
                    (c) => c.candidate.id,
                )
                if (candidateIds.length > 0) {
                    try {
                        const personsRes =
                            await personApi.getPersonsById(candidateIds)
                        const fullMap = new MapExtra<string, PersonDto>(
                            personsRes.persons.map((p) => [p.id, p]),
                        )
                        setCandidatePersonsById(fullMap)
                    } catch (e) {
                        console.warn(
                            'Failed to fetch full candidate persons',
                            e,
                        )
                        setCandidatePersonsById(new MapExtra())
                    }
                } else {
                    setCandidatePersonsById(new MapExtra())
                }

                // After we have role.hiringTeamIds and candidate personIds, fetch network data in batch
                const seedPersonIds = role.hiringTeamIds ?? []
                const resolvedPersonIds = recsRes.candidates.map(
                    (c) => c.candidate.id,
                )

                if (seedPersonIds.length > 0 && resolvedPersonIds.length > 0) {
                    try {
                        const networkRes = await networkApi.getNetworkScoped({
                            seedPersonIds,
                            resolvedPersonIds,
                        })

                        const transformed = transformNetworkScopedRes(
                            networkRes.resolvedPersonLites,
                        )

                        const connMap = new Map<
                            string,
                            TransformedResolvedPerson
                        >()
                        transformed.forEach((p) => connMap.set(p.personId, p))
                        setConnectionsByPersonId(connMap)

                        // Collect companyId and fetch details
                        const companyIdSet = new Set<string>()
                        transformed.forEach((p) => {
                            p.companies.forEach((c) =>
                                companyIdSet.add(c.companyId),
                            )
                        })
                        if (companyIdSet.size > 0) {
                            try {
                                const companiesRes =
                                    await companyApi.getCompanies({
                                        companyIds: Array.from(companyIdSet),
                                    })
                                const compMap = new MapExtra<
                                    string,
                                    CompanyDto
                                >(companiesRes.companies.map((c) => [c.id, c]))
                                setCompaniesById(compMap)
                            } catch (e) {
                                console.error(
                                    'Error fetching companies for connections',
                                    e,
                                )
                                setCompaniesById(new MapExtra())
                            }
                        } else {
                            setCompaniesById(new MapExtra())
                        }
                    } catch (e) {
                        console.error(
                            'Error fetching scoped network for candidates',
                            e,
                        )
                        setConnectionsByPersonId(new Map())
                        setCompaniesById(new MapExtra())
                    }
                } else {
                    // Nothing to fetch
                    setConnectionsByPersonId(new Map())
                    setCompaniesById(new MapExtra())
                }
            } catch (error) {
                console.error(error)
                if (
                    error instanceof Error &&
                    error.message.includes('Role not found')
                ) {
                    setRedirectToRoles(true)
                    return
                }
                const msg =
                    'There was a problem loading the role. Please refresh the page to try again. Error details: '
                if (error instanceof Error) {
                    setErrMsg(`${msg}${error.message}`)
                } else {
                    setErrMsg(`${msg}An unknown error occurred.`)
                }
            } finally {
                setIsLoading(false)
            }
        }
        load()
    }, [roleId])

    // Refresh only the Trusted Circle list and supporting person lites
    async function refreshTrustedCircle() {
        if (!roleId) return
        try {
            const sharedWithRes = await roleApi.getSharedWithUsersWithStatus({
                roleId,
            })
            setSharedWith(sharedWithRes.sharedWith)

            // Merge in any person lites for users that have a personId
            const personIds = sharedWithRes.sharedWith
                .map((s) => s.user.personId)
                .filter((id): id is string => Boolean(id))

            if (personIds.length > 0) {
                try {
                    const personsLite = await personApi.getPersonsLite({
                        personIds,
                    })
                    setPersonById((prev) => {
                        const merged = new MapExtra<string, PersonLiteDto>([
                            ...prev,
                            ...personsLite.persons.map(
                                (p) => [p.id, p] as const,
                            ),
                        ])
                        return merged
                    })
                } catch (e) {
                    console.warn(
                        'Failed to refresh person lites for trusted circle',
                        e,
                    )
                }
            }
        } catch (e) {
            console.error('Failed to refresh trusted circle after sharing', e)
        }
    }

    async function addHiringManagerModalClosed(shared: boolean) {
        setPendingHiringManagerIds([])
        setIsModalOpen(false)
        if (shared) {
            await refreshTrustedCircle()
        }
        // await refreshRole()
    }

    // Calculate review completion stats
    const totalReviewers = sharedWith.length
    const completedCount = sharedWith.filter(
        (s) => s.candidateReviewStatus === CandidateReviewStatus.COMPLETE,
    ).length
    const completedColor =
        completedCount === totalReviewers ? 'text-green-600' : 'text-red-500'

    const uniqueCandidateCount = React.useMemo(() => {
        const ids = new Set(recommendedCandidates.map((rc) => rc.candidate.id))
        return ids.size
    }, [recommendedCandidates])

    if (redirectToRoles || !roleId) {
        return <Navigate to={Pages.Roles} replace />
    }

    if (isLoading) {
        return (
            <LayoutSingleView activeMenuItem={MenuItems.Roles}>
                <LoadingSpinner />
            </LayoutSingleView>
        )
    }

    if (errMsg) {
        return (
            <LayoutSingleView activeMenuItem={MenuItems.Roles}>
                <Note type={NoteType.ERROR}>{errMsg}</Note>
            </LayoutSingleView>
        )
    }

    if (!roleLite) {
        return (
            <LayoutSingleView activeMenuItem={MenuItems.Roles}>
                <Note type={NoteType.ERROR}>Role not found.</Note>
            </LayoutSingleView>
        )
    }

    return (
        <LayoutSingleView activeMenuItem={MenuItems.Roles}>
            <Section className="mb-0">
                <div className="mb-2">
                    <RoleCard
                        role={roleLite}
                        clickable={false}
                        showJobDescription={true}
                    />
                </div>

                <div className="flex gap-2">
                    <Card className="flex flex-col items-center justify-center">
                        <div className="text-4xl font-bold text-ctxt-blue">
                            {uniqueCandidateCount}
                        </div>
                        <div className="text-sm text-gray-600 mt-1 text-center flex items-center gap-2">
                            Candidates
                        </div>
                    </Card>
                    <Card className="flex flex-col items-center justify-center">
                        <div className="text-2xl font-bold text-ctxt-blue">
                            <span className={`${completedColor} text-4xl`}>
                                {completedCount}
                            </span>
                            <span className="text-gray-500 text-sm">
                                {' '}
                                / {totalReviewers}
                            </span>
                        </div>
                        <div className="text-sm text-gray-600 mt-1 text-center flex items-center gap-2">
                            Reviews Complete
                        </div>
                    </Card>
                </div>
            </Section>

            <Section
                title="Trusted Circle"
                titleType="h3"
                className="my-0"
                buttons={
                    <Button
                        onClick={() => setIsModalOpen(true)}
                        style="secondary"
                        type="button"
                        size="small"
                        classes=""
                    >
                        <FontAwesomeIcon
                            icon={faUserPlus}
                            className="mr-2 text-ctxt-blue"
                        />
                        Invite
                    </Button>
                }
            >
                <TrustedCircleList
                    sharedWith={sharedWith}
                    personById={personById}
                />
            </Section>

            <Section
                title="Candidates"
                titleType="h3"
                collapsible={false}
                className="mt-0"
            >
                <RecommendedCandidatesList
                    candidates={recommendedCandidates}
                    personById={personById}
                    // New props
                    connectionsByPersonId={connectionsByPersonId}
                    companiesById={companiesById}
                    personFullById={candidatePersonsById}
                />
            </Section>

            {isModalOpen &&
                AuthHelper.userHasSomeRolesForTenant(
                    user,
                    [UserRoles.HM, UserRoles.TA],
                    tenant.id,
                ) && (
                    <InviteUsersModal
                        onClose={addHiringManagerModalClosed}
                        roleTitle={roleLite.jobTitle}
                        roleId={roleId!}
                        tenantId={tenant.id}
                        sharedWith={sharedWith}
                        personById={personById}
                        tenantCompanyIds={tenant.companyIds}
                    />
                )}
        </LayoutSingleView>
    )
}
